import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { SelectOption } from 'app/_componentes/select/_models/selectOption';
import { QuotationPartTypeEnum } from 'app/_models/_enums/quotationPartType.enum';
import { QuotationPart, quotationPartFactory } from '../../_models/quotationPart';
import { Globals } from 'app/_models/globals';
import { NotificationService } from 'app/_services/notification.service';

@Component({
    selector: 'quotation-parts-component', templateUrl: './quotation-parts.component.html', styleUrls: ['./quotation-parts.component.scss'],
    standalone: false
})
export class BuyerCreateQuotationPartsComponent implements OnInit {
    @Input() isQuotationWithParts: boolean = true;
    @Output() onPartListChange: EventEmitter<{ partsCount: number }> = new EventEmitter<{ partsCount: number }>()

    private ptBRPath: string = 'buyer.create-quotation.quotation-parts'

    public allowsUsedOptions: SelectOption[] = [
        { value: true, name: 'elemento.generico-select.sim' },
        { value: false, name: 'elemento.generico-select.nao' },
    ];

    public types: SelectOption[] = [
        { value: QuotationPartTypeEnum.Genuina, name: `${this.ptBRPath}.part-type.${QuotationPartTypeEnum[QuotationPartTypeEnum.Genuina].toLowerCase()}` },
        { value: QuotationPartTypeEnum.Original, name: `${this.ptBRPath}.part-type.${QuotationPartTypeEnum[QuotationPartTypeEnum.Original].toLowerCase()}` },
        { value: QuotationPartTypeEnum.Alternativa, name: `${this.ptBRPath}.part-type.${QuotationPartTypeEnum[QuotationPartTypeEnum.Alternativa].toLowerCase()}` },
        { value: QuotationPartTypeEnum.Importada, name: `${this.ptBRPath}.part-type.${QuotationPartTypeEnum[QuotationPartTypeEnum.Importada].toLowerCase()}` },
    ];

    public parts: QuotationPart[] = [];

    fb = inject(FormBuilder);

    partNameControl: FormControl<string>;
    partNumberControl: FormControl<string>;
    quantityControl: FormControl<string>;
    typeControl: FormControl<QuotationPartTypeEnum[]>;
    allowsUsedControl: FormControl<boolean>;

    partFormGroup = this.fb.group({
        partName: ["", [Validators.required]],
        partNumber: [""],
        quantity: [1, [Validators.required, Validators.min(1), Validators.max(999)]],
        type: [[QuotationPartTypeEnum.Genuina] as QuotationPartTypeEnum[], [Validators.required]],
        allowsUsed: [false, [Validators.required]]
    });

    validationMessages: { partName: any, quantity: any, type: any, allowsUsed: any };

    constructor(private notificationService: NotificationService, private globals: Globals) {
        this.validationMessages = {
            partName: {
                required: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-name.required'),
            },
            quantity: {
                required: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-quantity.required'),
                min: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-quantity.min'),
                max: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-quantity.max'),
            },
            type: {
                required: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-type.required')
            },
            allowsUsed: {
                required: this.globals.translate('buyer.create-quotation.quotation-parts.form.validation-message.part-condition.required')
            }
        };
    }

    ngOnInit() {
        this.partNameControl = this.partFormGroup.get('partName') as FormControl;
        this.partNumberControl = this.partFormGroup.get('partNumber') as FormControl;
        this.quantityControl = this.partFormGroup.get('quantity') as FormControl;
        this.typeControl = this.partFormGroup.get('type') as FormControl;
        this.allowsUsedControl = this.partFormGroup.get('allowsUsed') as FormControl;
    }

    addPart() {
        if (!this.partFormGroup.touched || !this.isFormValid()) {
            return;
        }

        const formValue = this.partFormGroup.value;
        const part = quotationPartFactory(formValue.partName, formValue.quantity, formValue.partNumber, formValue.type, formValue.allowsUsed)

        this.parts.push(part);
        this.partFormGroup.reset();

        if (this.isQuotationWithParts) {
            this.partFormGroup.patchValue({
                quantity: 1,
                type: [QuotationPartTypeEnum.Genuina],
                allowsUsed: false
            });
        }
        else {
            this.partFormGroup.patchValue({
                quantity: 1
            });
        }
        
        this.onPartListChange.emit({ partsCount: this.parts.length });
    }

    updateQuotationParts(isQuotationWithParts: boolean) {
        this.parts = [];
        this.partFormGroup.reset();
        this.onPartListChange.emit({ partsCount: this.parts.length });

        if (isQuotationWithParts) {
            this.partFormGroup.controls.type.setValidators([Validators.required])
            this.partFormGroup.controls.allowsUsed.setValidators([Validators.required])

            this.partFormGroup.patchValue({
                quantity: 1,
                type: [QuotationPartTypeEnum.Genuina],
                allowsUsed: false,
            })
        } else {
            this.partFormGroup.controls.type.clearValidators();
            this.partFormGroup.controls.allowsUsed.clearValidators();

            this.partFormGroup.patchValue({
                quantity: 1,
            })
        }

        this.partFormGroup.controls.type.updateValueAndValidity();
        this.partFormGroup.controls.allowsUsed.updateValueAndValidity();
    }

    removePart(partIndex: number) {
        this.parts.splice(partIndex, 1);
        this.onPartListChange.emit({ partsCount: this.parts.length });
    }

    private isFormValid() {
        this.notificationService.clearToastr();

        Object.keys(this.partFormGroup.controls).forEach(field => {
            const control = this.partFormGroup.get(field);
            control?.markAsTouched({ onlySelf: true });
        });

        if (this.partFormGroup.valid)
            return true;

        Object.keys(this.validationMessages).forEach(field => {
            const control = this.partFormGroup.get(field);
            const errorMessages = this.validationMessages[field];

            if (control?.invalid) {
                Object.keys(errorMessages).forEach(errorKey => {
                    if (control.errors?.[errorKey]) {
                        this.notificationService.showErrorToastr(errorMessages[errorKey]);
                    }
                });
            }
        });

        return false;
    }
}
